import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import type { IntlShape } from 'react-intl'
import { FlightFilterType } from 'constants/flight'

export function getFlightFilterLabel(intl: IntlShape, filterType: string): string {
  switch (filterType) {
    case FlightFilterType.STOPS:
      return intl.formatMessage({
        id: 'XOD4CZs2+5',
        defaultMessage: 'Stops',
        description: 'Flight filter label for number of stops',
      })
    case FlightFilterType.AIRLINES:
      return intl.formatMessage({
        id: 'v9iV+qqLDr',
        defaultMessage: 'Airlines',
        description: 'Flight filter label for airline selection',
      })
    case FlightFilterType.STOP_DURATIONS:
      return intl.formatMessage({
        id: '6CU9YQpAng',
        defaultMessage: 'Stopover time',
        description: 'Flight filter label for stopover duration',
      })
    case FlightFilterType.CABIN_TYPES:
      return intl.formatMessage({
        id: 'QAVnSAZ/Es',
        defaultMessage: 'Cabin',
        description: 'Flight filter label for cabin class selection',
      })
    case FlightFilterType.DURATIONS:
      return intl.formatMessage({
        id: 'om+ebJUvR2',
        defaultMessage: 'Duration',
        description: 'Flight filter label for flight duration',
      })
    case FlightFilterType.PRICES:
      return intl.formatMessage({
        id: 'l6p9avTXod',
        defaultMessage: 'Prices',
        description: 'Flight filter label for price range',
      })
    case FlightFilterType.TIMES:
      return intl.formatMessage({
        id: 't6pfnMU+iR',
        defaultMessage: 'Times',
        description: 'Flight filter label for departure/arrival times',
      })
    case FlightFilterType.BAGS:
      return intl.formatMessage({
        id: 'kUCQGvi3yL',
        defaultMessage: 'Bags',
        description: 'Flight filter label for baggage options',
      })
    case FlightFilterType.STOP_OVER_CITIES:
      return intl.formatMessage({
        id: 'S029hPCq9v',
        defaultMessage: 'Stopover cities',
        description: 'Flight filter label for stopover city selection',
      })
    case FlightFilterType.ALLIANCES:
      return intl.formatMessage({
        id: 'DZK7q3mrm+',
        defaultMessage: 'Alliances',
        description: 'Flight filter label for airline alliance selection',
      })
    case FlightFilterType.APPLIED_FILTERS:
      return intl.formatMessage({
        id: 'IXyQDvLJ/i',
        defaultMessage: 'Applied filters',
        description: 'Flight filter label for currently applied filters',
      })
    default:
      return filterType
  }
}

export function getFlightFilterModalLabel(intl: IntlShape, filterType: string): string {
  if (filterType === FlightFilterType.DURATIONS) {
    return intl.formatMessage({
      id: '56ESkKjoWI',
      defaultMessage: 'Flight duration',
      description: 'Flight filter modal label for flight duration filter',
    })
  }
  if (filterType === FlightFilterType.RECOMMENDED) {
    return intl.formatMessage({
      id: 'HtcT5u5+zi',
      defaultMessage: 'Recommended filters',
      description: 'Flight filter modal label for recommended filters section',
    })
  }
  return getFlightFilterLabel(intl, filterType)
}

function useFlightFilterLabels() {
  const intl = useIntl()

  return useMemo(() => {
    const flightFiltersLabelMap: Record<string, string> = {
      [FlightFilterType.STOPS]: getFlightFilterLabel(intl, FlightFilterType.STOPS),
      [FlightFilterType.AIRLINES]: getFlightFilterLabel(intl, FlightFilterType.AIRLINES),
      [FlightFilterType.STOP_DURATIONS]: getFlightFilterLabel(intl, FlightFilterType.STOP_DURATIONS),
      [FlightFilterType.CABIN_TYPES]: getFlightFilterLabel(intl, FlightFilterType.CABIN_TYPES),
      [FlightFilterType.DURATIONS]: getFlightFilterLabel(intl, FlightFilterType.DURATIONS),
      [FlightFilterType.PRICES]: getFlightFilterLabel(intl, FlightFilterType.PRICES),
      [FlightFilterType.TIMES]: getFlightFilterLabel(intl, FlightFilterType.TIMES),
      [FlightFilterType.BAGS]: getFlightFilterLabel(intl, FlightFilterType.BAGS),
      [FlightFilterType.STOP_OVER_CITIES]: getFlightFilterLabel(intl, FlightFilterType.STOP_OVER_CITIES),
      [FlightFilterType.ALLIANCES]: getFlightFilterLabel(intl, FlightFilterType.ALLIANCES),
      [FlightFilterType.APPLIED_FILTERS]: getFlightFilterLabel(intl, FlightFilterType.APPLIED_FILTERS),
    }

    const flightFiltersModalLabelMap: Record<string, string> = {
      [FlightFilterType.STOPS]: getFlightFilterModalLabel(intl, FlightFilterType.STOPS),
      [FlightFilterType.AIRLINES]: getFlightFilterModalLabel(intl, FlightFilterType.AIRLINES),
      [FlightFilterType.STOP_DURATIONS]: getFlightFilterModalLabel(intl, FlightFilterType.STOP_DURATIONS),
      [FlightFilterType.CABIN_TYPES]: getFlightFilterModalLabel(intl, FlightFilterType.CABIN_TYPES),
      [FlightFilterType.DURATIONS]: getFlightFilterModalLabel(intl, FlightFilterType.DURATIONS),
      [FlightFilterType.PRICES]: getFlightFilterModalLabel(intl, FlightFilterType.PRICES),
      [FlightFilterType.TIMES]: getFlightFilterModalLabel(intl, FlightFilterType.TIMES),
      [FlightFilterType.RECOMMENDED]: getFlightFilterModalLabel(intl, FlightFilterType.RECOMMENDED),
      [FlightFilterType.BAGS]: getFlightFilterModalLabel(intl, FlightFilterType.BAGS),
      [FlightFilterType.STOP_OVER_CITIES]: getFlightFilterModalLabel(intl, FlightFilterType.STOP_OVER_CITIES),
      [FlightFilterType.ALLIANCES]: getFlightFilterModalLabel(intl, FlightFilterType.ALLIANCES),
      [FlightFilterType.APPLIED_FILTERS]: getFlightFilterModalLabel(intl, FlightFilterType.APPLIED_FILTERS),
    }

    const flightFilters: Array<{ label: string; value: FlightFilterType }> = [
      { label: flightFiltersLabelMap[FlightFilterType.STOPS], value: FlightFilterType.STOPS },
      { label: flightFiltersLabelMap[FlightFilterType.AIRLINES], value: FlightFilterType.AIRLINES },
      { label: flightFiltersLabelMap[FlightFilterType.STOP_DURATIONS], value: FlightFilterType.STOP_DURATIONS },
      { label: flightFiltersLabelMap[FlightFilterType.CABIN_TYPES], value: FlightFilterType.CABIN_TYPES },
      { label: flightFiltersLabelMap[FlightFilterType.DURATIONS], value: FlightFilterType.DURATIONS },
      { label: flightFiltersLabelMap[FlightFilterType.BAGS], value: FlightFilterType.BAGS },
      { label: flightFiltersLabelMap[FlightFilterType.STOP_OVER_CITIES], value: FlightFilterType.STOP_OVER_CITIES },
      { label: flightFiltersLabelMap[FlightFilterType.ALLIANCES], value: FlightFilterType.ALLIANCES },
    ]

    return {
      flightFiltersLabelMap,
      flightFiltersModalLabelMap,
      flightFilters,
    }
  }, [intl])
}

export default useFlightFilterLabels
